import { LitElement, html } from 'lit';
import styles from './studio-upload.css.mjs';
import '../studio-text/studio-text.mjs';
import '../studio-button/studio-button.mjs';
import '../studio-progress/studio-progress.mjs';
import '../studio-loader/studio-loader.mjs';
import '../studio-upload-meta/studio-upload-meta.mjs';

export class StudioUpload extends LitElement {
  static styles = styles;

  static properties = {
    dragging: { type: Boolean, reflect: true },
    multiple: { type: Boolean },
    accept: { type: String },
    step: { type: String },
    pendingFiles: { type: Array },
    uploadingFiles: { type: Array },
    currentIndex: { type: Number },
    uploading: { type: Boolean },
    showDropdown: { type: Boolean },
    createdBy: { type: String },
    createdAt: { type: String },
    isPlayingCurrent: { type: Boolean },
    currentVideoUrl: { type: String },
    nextButtonState: { type: String }, // Added to manage button state
    forceFiles: { type: Array },
  };

  iconMapping = {
    'jpg': 'image',
    'jpeg': 'image',
    'png': 'image',
    'gif': 'image',
    'mp3': 'headphones',
    'mp4': 'headphones',
    'mov': 'film',
  };

  fileIdCounter = 0;

  constructor() {
    super();
    this.dragging = false;
    this.multiple = false;
    this.accept = '';
    this.step = 'drop';
    this.pendingFiles = [];
    this.uploadingFiles = [];
    this.currentIndex = 0;
    this.uploading = false;
    this.showDropdown = false;
    this.createdBy = 'Unknown';
    this.updatedBy = 'Unknown';
    this.isPlayingCurrent = false;
    this.currentVideoUrl = null;
    this.nextButtonState = ''; // Initialize button state
    this._handleOutsideClick = this._handleOutsideClick.bind(this);
    this.forceFiles = null;
  }

  _onDragOver(evt) {
    evt.preventDefault();
    evt.dataTransfer.dropEffect = 'copy';
  }

  _onDragEnter(evt) {
    evt.preventDefault();
    this.dragging = true;
  }

  _onDragLeave() {
    this.dragging = false;
  }

  _onDrop(evt) {
    evt.preventDefault();
    this.dragging = false;
    if (this.forceFiles) { return; }
    this._handleFiles(evt.dataTransfer.files);
  }

  _onClick() {
    this.renderRoot.getElementById('fileInput').click();
  }

  _onFileChange(evt) {
    this._handleFiles(evt.target.files);
  }

  async _handleFiles(fileList) {
    const files = Array.from(fileList);
    const processedFiles = [];
    const createdAt = this.createdAt || new Date().toISOString();

    for (const file of files) {
      const parts = file.name.split('.');
      const extension = parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
      const isImageExtension = ['jpg', 'jpeg', 'png', 'gif', 'heic', 'heif'].includes(extension);
      const fileType = isImageExtension || file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : 'other';
      const isHeic = ['heic', 'heif'].includes(extension);
      const canGenerateThumbnail = fileType === 'image' ? !isHeic : (fileType === 'video' && file.size <= 20 * 1024 * 1024); // 20MB limit for videos
      processedFiles.push({
        id: this.fileIdCounter++,
        file,
        progress: 0,
        dataUrl: null,
        fileType,
        canGenerateThumbnail,
        thumbnailUrl: null,
        isGeneratingThumbnail: false,
        uploadStatus: null,
        metadata: {
          name: file.name,
          caption: '',
          credit: '',
          'created-at': createdAt,
          'created-by': this.createdBy,
					'updated-at': createdAt,
					'updated-by': this.updatedBy,
        },
      });
    }

    if (this.step === 'list') {
      this.pendingFiles = [...this.pendingFiles, ...processedFiles];
    } else {
      this.pendingFiles = processedFiles;
      this.step = 'list';
    }

    processedFiles.forEach((fileItem) => {
      if (fileItem.fileType === 'image' && fileItem.canGenerateThumbnail) {
        this.readFile(fileItem);
      } else {
        fileItem.progress = 100; // For videos, 'other' files, and HEIC images
        this.requestUpdate();
      }
    });
  }

  async readFile(fileItem) {
    const reader = new FileReader();
    const id = fileItem.id;

    reader.onprogress = (event) => {
      if (event.lengthComputable) {
        const progress = Math.round((event.loaded / event.total) * 100);
        this.updateFileProgress(id, progress);
      }
    };

    reader.onload = () => {
      this.updateFileProgress(id, 100);
      this.updateFileDataUrl(id, reader.result);
    };

    reader.onerror = () => {
      console.error('Error reading file:', fileItem.file.name);
    };

    reader.readAsDataURL(fileItem.file);
  }

  async generateThumbnailForFile(fileItem) {
    if (fileItem.fileType !== 'video' || !fileItem.canGenerateThumbnail) return;
    if (fileItem.isGeneratingThumbnail || fileItem.thumbnailUrl) return;

    fileItem.isGeneratingThumbnail = true;
    this.requestUpdate();

    try {
      const url = URL.createObjectURL(fileItem.file);
      const video = document.createElement('video');
      video.src = url;

      await new Promise((resolve) => {
        video.onloadeddata = resolve;
      });

      video.currentTime = 1; // Seek to 1 second
      await new Promise((resolve) => {
        video.onseeked = resolve;
      });

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      fileItem.thumbnailUrl = canvas.toDataURL('image/jpeg');
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating thumbnail:', error);
      fileItem.thumbnailUrl = null; // Fallback to no thumbnail
    } finally {
      fileItem.isGeneratingThumbnail = false;
      this.requestUpdate();
    }
  }

  updateFileProgress(id, progress) {
    this.pendingFiles = this.pendingFiles.map(file =>
      file.id === id ? { ...file, progress } : file
    );
  }

  updateFileDataUrl(id, dataUrl) {
    this.pendingFiles = this.pendingFiles.map(file =>
      file.id === id ? { ...file, dataUrl } : file
    );
  }

  setFileUploadStatus(id, status) {
    this.uploadingFiles = this.uploadingFiles.map(file =>
      file.id === id ? { ...file, uploadStatus: status } : file
    );
    this._checkUploadStatus();
  }

  _cancelUploadingFile(id) {
    this.dispatchEvent(new CustomEvent('cancel-upload', {
      detail: { id },
      bubbles: true,
      composed: true,
    }));
    this.uploadingFiles = this.uploadingFiles.filter(file => file.id !== id);
    this._checkUploadStatus();
  }

  _checkUploadStatus() {
    if (this.uploadingFiles.length === 0 || this.uploadingFiles.every(file => file.uploadStatus === 'done')) {
      this.uploading = false;
    } else {
      this.uploading = true;
    }
  }

  _remove(id) {
  	// remove the file from pendingFiles
  	this.pendingFiles = this.pendingFiles.filter(file => file.id !== id);

  	// If we were on the "submit" step and there are no more files, close the modal
  	if (this.step === 'submit' && this.pendingFiles.length === 0) {
    	this.step = 'drop';    // hides the modal
  	}
	}

  async _back() {
    this._stopPlayingCurrentVideo();
    if (this.step === 'submit') {
      this.step = 'view';
      this.currentIndex = this.pendingFiles.length - 1;
      await this.updateComplete; // Ensure DOM updates
    } else if (this.step === 'view') {
      if (this.currentIndex > 0) {
        this.currentIndex--;
        await this.updateComplete; // Ensure DOM updates
      } else {
        this.step = 'list';
        await this.updateComplete; // Ensure DOM updates
      }
    } else if (this.step === 'list') {
      this.step = 'drop';
      this.pendingFiles = [];
      if (this.processForced) {
        this.dispatchEvent(new CustomEvent('force-upload-canceled', {
          bubbles: true,
          composed: true
        }));
      }
      await this.updateComplete; // Ensure DOM updates
    }
    this.updateNextButtonState(); // Update button state after navigation
  }

  async _next() {
    this._stopPlayingCurrentVideo();
    if (this.step === 'list' && this.pendingFiles.length > 0) {
      this.step = 'view';
      this.currentIndex = 0;
      await this.updateComplete; // Ensure DOM updates
    } else if (this.step === 'view') {
      const data = this._getMetadata();
      if (data) {
        this.handleUpdateAsset(data);
      }

      if (this.currentIndex < this.pendingFiles.length - 1) {
        this.currentIndex++;
        await this.updateComplete; // Ensure DOM updates
      } else {
        this.step = 'submit';
        await this.updateComplete; // Ensure DOM updates
      }
    }
    this.updateNextButtonState(); // Update button state after navigation
  }

  _getMetadata() {
    const currentMeta = this.renderRoot.querySelector('studio-upload-meta.active');
    if (currentMeta) {
      return currentMeta.gatherMetadata();
    } else {
      console.error('Active meta component not found');
      return null;
    }
  }

	/**
 	* Return true if this file’s metadata meets the “required fields” rule.
 	* We want at least a non‐empty caption (and we could add
 	* other checks here later if needed).
 	*/
	hasValidMetadata(fileItem) {
  	const { name, caption } = fileItem.metadata;
  	return (
    	name &&
    	name.trim() !== '' &&
    	caption &&
    	caption.trim() !== '' &&
    	true
  	);
	}


  _submit() {
    const filesToUpload = this.pendingFiles.map(file => ({ ...file, uploadStatus: 'uploading' }));
    this.uploadingFiles = [...this.uploadingFiles, ...filesToUpload];
    this.dispatchEvent(
      new CustomEvent('files-submitted', {
        detail: { files: filesToUpload },
        bubbles: true,
        composed: true,
      })
    );
    this.pendingFiles = [];
    this.step = 'drop';
    this.uploading = true;
    this._stopPlayingCurrentVideo();
  }

  _toggleDropdown() {
    this.showDropdown = !this.showDropdown;
  }

  _playCurrentVideo() {
    const currentFile = this.pendingFiles[this.currentIndex];
    if (currentFile.fileType === 'video' && currentFile.canGenerateThumbnail) {
      this.isPlayingCurrent = true;
      this.currentVideoUrl = URL.createObjectURL(currentFile.file);
      this.requestUpdate();
    }
  }

  _stopPlayingCurrentVideo() {
    if (this.isPlayingCurrent) {
      this.isPlayingCurrent = false;
      if (this.currentVideoUrl) {
        URL.revokeObjectURL(this.currentVideoUrl);
        this.currentVideoUrl = null;
      }
      this.requestUpdate();
    }
  }

  getIconName(extension, fileType = '') {
    if (extension && this.iconMapping[extension.toLowerCase()]) {
      return this.iconMapping[extension.toLowerCase()];
    } else if (fileType === 'image') {
      return 'image';
    } else if (fileType === 'video') {
      return 'film';
    } else {
      return 'file';
    }
  }

  getConfigForFile(index) {
    const fileItem = this.pendingFiles[index];
    const file = fileItem.file;
    return {
      name: fileItem.metadata.name || '',
      'content-type': file.type || '',
      'content-length': file.size || 0,
      id: fileItem.id,
      caption: fileItem.metadata.caption || '',
      credit: fileItem.metadata.credit || '',
      'updated-at': fileItem.metadata['updated-at'],
      'updated-by': fileItem.metadata['updated-by'],
    };
  }

  handleDeleteAsset(event) {
		const id = event.detail;
    this._remove(id);
    if (this.pendingFiles.length === 0) {
      this.step = 'list';
    } else if (this.currentIndex >= this.pendingFiles.length) {
      this.currentIndex = this.pendingFiles.length - 1;
    }
    this.requestUpdate();
    this.updateNextButtonState(); // Update button state after deletion
  }

  handleUpdateAsset(updatedConfig) {
    const fileItem = this.pendingFiles.find(f => f.id === updatedConfig.id);
    if (fileItem) {
      fileItem.metadata = {
        ...fileItem.metadata,
        name: updatedConfig.name,
        caption: updatedConfig.caption,
        credit: updatedConfig.credit,
      };
      this.requestUpdate();
      this.updateNextButtonState(); // Update button state after metadata change
    }
  }

  _renderDropzone(options) {
    const { mainVariant, subVariant, isModal = false } = options;
    const iconSize = isModal ? '25px' : '35px';
    const mainText = html`
      Drag & drop <span style="color: var(--color-app-dam)">images</span>,
      <span style="color: var(--color-app-dam)">videos</span>,
      or any <span style="color: var(--color-app-dam)">file</span>
    `;
    const subText = html`
      <span>or</span>
      <span style="color: var(--color-app-dam); text-decoration: underline;">
        browse files</span>
      <span>from your computer</span>
    `;
    return html`
      <div
        class="dropzone"
        @dragover=${this._onDragOver}
        @dragenter=${this._onDragEnter}
        @dragleave=${this._onDragLeave}
        @drop=${this._onDrop}
        @click=${this._onClick}
      >
        <studio-icon
          name="upload"
          .size=${iconSize}
          color="var(--color-app-dam)"
        ></studio-icon>
        <studio-text variant=${mainVariant}> ${mainText} </studio-text>
        <studio-text variant=${subVariant}> ${subText} </studio-text>
      </div>
    `;
  }

  _renderFileList() {
    return html`
      <div class="file-list">
        <div class="header-cell" style="grid-column: 1; grid-row: 1;">
          <studio-text variant="c3-caption">File type</studio-text>
        </div>
        <div class="header-cell" style="grid-column: 2; grid-row: 1;">
          <studio-text variant="c3-caption">File name</studio-text>
        </div>
        <div class="header-cell" style="grid-column: 3; grid-row: 1;">
          <studio-text variant="c3-caption">File size</studio-text>
        </div>
        <div class="header-cell" style="grid-column: 4; grid-row: 1;"></div>
        ${this.pendingFiles.map((item, idx) => {
          const isFirst = idx === 0;
          const isLast = idx === this.pendingFiles.length - 1;
          const rowStart = 2 + idx * 2;
          const { file, progress } = item;
          const parts = file.name.split('.');
          const hasExtension = parts.length > 1;
          const extension = hasExtension ? parts.pop().toLowerCase() : '';
          const extDisplay = hasExtension ? extension.toUpperCase() : (item.fileType === 'image' ? 'IMAGE' : item.fileType === 'video' ? 'VIDEO' : 'FILE');
          const iconName = this.getIconName(extension, item.fileType);
          const sizeKB = (file.size / 1024).toFixed(2);

					const isCheckVisible = (this.step === 'list' || this.step === 'submit')
						&& this.hasValidMetadata(item);

          const fileTypeStyle = [
            `grid-column: 1; grid-row: ${rowStart} / span 2;`,
            isFirst ? 'border-top-left-radius: 10px;' : '',
            isLast ? 'border-bottom-left-radius: 10px;' : '',
          ].join(' ');

          const deleteIconStyle = [
            `grid-column: 4; grid-row: ${rowStart} / span 2;`,
            isFirst ? 'border-top-right-radius: 10px;' : '',
            isLast ? 'border-bottom-right-radius: 10px;' : '',
          ].join(' ');

          return html`
            <div class="file-type-cell" style=${fileTypeStyle}>
              ${isCheckVisible
                ? html`<studio-icon name="check" class="check-icon"></studio-icon>`
                : ''}
              <studio-text class="file-type" variant="c3-caption">${extDisplay}</studio-text>
              <studio-icon
                class="icon ${iconName}"
                name=${iconName}
                aria-hidden="true"
              ></studio-icon>
            </div>
            <div class="file-name" style="grid-column: 2; grid-row: ${rowStart};">
              <studio-text variant="c3-caption">${item.metadata.name || file.name}</studio-text>
            </div>
            <div class="file-size" style="grid-column: 3; grid-row: ${rowStart};">
              <studio-text variant="c3-caption">${sizeKB} KB</studio-text>
            </div>
            <div class="delete-icon-cell" style=${deleteIconStyle}>
              ${this.processForced ? '' : html`
                <studio-icon
                  class="delete-icon"
                  name=${progress === 100 ? 'trash' : 'circle-xmark'}
                  @click=${() => this._remove(item.id)}
                ></studio-icon>
              `}
            </div>
            <div
              class="file-progress-cell"
              style="grid-column: 2 / span 2; grid-row: ${rowStart + 1};"
            >
              <studio-progress
                percent=${progress}
                position="left"
                color=${progress === 100
                  ? 'var(--color-green-light, #00B37E)'
                  : 'var(--color-purple-light, #4727A5)'}
              ></studio-progress>
            </div>
          `;
        })}
      </div>
    `;
  }

  _renderThumbnailSlot(fileItem, index) {
    if (fileItem.fileType === 'image') {
      const parts = fileItem.file.name.split('.');
      const extension = parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
      const isHeic = ['heic', 'heif'].includes(extension);
      if (isHeic) {
        return html`
          <studio-icon slot="thumbnail" name="image"></studio-icon>
        `;
      } else {
        return html`
          <img
            slot="thumbnail"
            src=${fileItem.dataUrl}
            alt=${fileItem.metadata.name || fileItem.file.name}
            loading="lazy"
          />
        `;
      }
    } else if (fileItem.fileType === 'video' && fileItem.canGenerateThumbnail) {
      if (this.isPlayingCurrent && this.currentIndex === index) {
        return html`
          <video slot="thumbnail" src=${this.currentVideoUrl} controls></video>
        `;
      } else if (fileItem.isGeneratingThumbnail) {
        return html`
          <studio-loader slot="thumbnail"></studio-loader>
        `;
      } else if (fileItem.thumbnailUrl) {
        return html`
          <div slot="thumbnail" @click=${this._playCurrentVideo} style="position: relative; cursor: pointer;">
            <img
              src=${fileItem.thumbnailUrl}
              alt=${fileItem.metadata.name || fileItem.file.name}
              style="max-width: 100%; max-height: 300px; display: block;"
            />
            <studio-icon
              name="video"
              style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);"
            ></studio-icon>
          </div>
        `;
      } else {
        return html`
          <studio-text slot="thumbnail">Generating thumbnail...</studio-text>
        `;
      }
    } else {
      const parts = fileItem.file.name.split('.');
      const hasExtension = parts.length > 1;
      const extension = hasExtension ? parts.pop().toLowerCase() : '';
      const iconName = this.getIconName(extension, fileItem.fileType);
      return html`
        <studio-icon slot="thumbnail" name=${iconName}></studio-icon>
      `;
    }
  }

  _handleOutsideClick(event) {
    const dropdown = this.renderRoot.querySelector('.dropdown');
    if (dropdown && !dropdown.contains(event.target)) {
      this.showDropdown = false;
    }
  }

  updated(changedProperties) {
  	super.updated(changedProperties);
  	if (this.step === 'view') {
    	const currentFile = this.pendingFiles[this.currentIndex];
    	if (
      	currentFile.fileType === 'video' &&
      	currentFile.canGenerateThumbnail &&
      	!currentFile.thumbnailUrl &&
      	!currentFile.isGeneratingThumbnail
    	) {
      	this.generateThumbnailForFile(currentFile);
    	}
  	}
  	if (changedProperties.has('showDropdown')) {
    	if (this.showDropdown) {
      	setTimeout(() => {
        	document.addEventListener('click', this._handleOutsideClick);
      	}, 0);
    	} else {
      	document.removeEventListener('click', this._handleOutsideClick);
    	}
  	}
  	// Add 'pendingFiles' to the condition
  	if (
			changedProperties.has('step') || 
			changedProperties.has('currentIndex') || 
			changedProperties.has('pendingFiles')
		) {
    	this.updateNextButtonState();
  	}
	}
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this._handleOutsideClick);
    this._stopPlayingCurrentVideo();
  }

  async updateNextButtonState() {
    await this.updateComplete; // Wait for DOM to be fully updated
    const data = this._getMetadata();
    if (this.step === 'list' && this.pendingFiles.length === 0) {
      this.nextButtonState = 'disabled';
    } else if (this.step === 'view') {
      if (
        data &&
        data.name &&
        data.name.trim() !== '' &&
        data.caption &&
        data.caption.trim() !== '' &&
        data.caption.trim().length <= 200
      ) {
        this.nextButtonState = '';
      } else {
        this.nextButtonState = 'disabled';
      }
    } else {
      this.nextButtonState = '';
    }
  }

  _handleMetaChange() {
    this.requestUpdate();
    this.updateNextButtonState(); // Update button state when metadata changes
  }

  render() {
    const allCaptionsFilled = this.pendingFiles.every(file => file.metadata.caption && file.metadata.caption.trim() !== '');

    if (this.forceFiles && !this.processForced) {
      this._handleFiles(this.forceFiles);
      this.processForced = true;
      this.step = 'list';
    }

    return html`
      ${this.processForced ? 
          html`<studio-loader size="medium"></studio-loader>` :
          this._renderDropzone({ mainVariant: 'h3', subVariant: 's1' })
      }

      ${this.uploading
        ? html`
            <div
              class="upload-pill ${this.showDropdown ? 'expanded' : ''}"
              part="upload-pill"
              @click=${this._toggleDropdown}
            >
              <div class="spacer"></div>
              <div class="status" style="display: flex; align-itemsAlignment: center;">
                ${!this.showDropdown &&
                this.uploadingFiles.some(file => file.uploadStatus === 'uploading')
                  ? html`<studio-loader
                      size="tiny"
                      style="margin-right: 8px;"
                    ></studio-loader>`
                  : ''}
                ${this.uploadingFiles.some(file => file.uploadStatus === 'uploading')
                  ? html`<studio-text variant="c1-caption" style="align-content: center;">
                      Uploading | ${this.uploadingFiles.filter(file => file.uploadStatus === 'done').length}
                      of ${this.uploadingFiles.length} files done
                    </studio-text>`
                  : html`
                      <studio-icon name="check-circle"></studio-icon>
                      <studio-text variant="c1-caption">Upload complete</studio-text>
                    `}
              </div>
              <studio-icon
                name=${this.showDropdown ? 'chevron-up' : 'chevron-down'}
              ></studio-icon>
              ${this.showDropdown
                ? html`
                    <div class="dropdown" @click=${(e) => e.stopPropagation()}>
                      ${this.uploadingFiles.map((file, index) => {
                        const parts = file.file.name.split('.');
                        const hasExtension = parts.length > 1;
                        const extension = hasExtension ? parts.pop().toLowerCase() : '';
                        const extDisplay = hasExtension ? extension.toUpperCase() : (file.fileType === 'image' ? 'IMAGE' : file.fileType === 'video' ? 'VIDEO' : 'FILE');
                        const iconName = this.getIconName(extension, file.fileType);

                        let positionClass = '';
                        if (this.uploadingFiles.length === 1) {
                          positionClass = 'file-entry-only';
                        } else if (index === 0) {
                          positionClass = 'file-entry-first';
                        } else if (index === this.uploadingFiles.length - 1) {
                          positionClass = 'file-entry-last';
                        } else {
                          positionClass = 'file-entry-middle';
                        }

                        return html`
                          <div class="file-entry ${positionClass}">
                            <div class="status-icon">
                              ${file.uploadStatus === 'uploading'
                                ? html`<studio-loader size="tiny"></studio-loader>`
                                : html`<studio-icon
                                    name="check"
                                    style="color: var(--color-green-light, #00B37E);"
                                  ></studio-icon>`}
                            </div>
                            <div class="file-type-info">
                              <studio-text variant="c3-caption">${extDisplay}</studio-text>
                              <studio-icon
                                class="file-type-icon"
                                name=${iconName}
                                aria-hidden="true"
                              ></studio-icon>
                            </div>
                            <div class="file-name">
                              <studio-text variant="c3-caption">${file.metadata.name || file.file.name}</studio-text>
                            </div>
                            ${file.uploadStatus === 'uploading'
                              ? html`<studio-icon
                                  class="delete-icon"
                                  name="circle-xmark"
                                  @click=${() => this._cancelUploadingFile(file.id)}
                                ></studio-icon>`
                              : ''}
                          </div>
                        `;
                      })}
                    </div>
                  `
                : ''}
            </div>
          `
        : ''}

      <input
        id="fileInput"
        type="file"
        name="files[]"
        ?multiple=${this.multiple === true} 
        accept=${this.accept}
        @change=${this._onFileChange}
        hidden
      />

      ${this.step !== 'drop'
        ? html`
            <div class="modal-backdrop">
              <div class="upload-modal" part="modal">
                ${this.step === 'list'
                  ? html`
                      <studio-text
                        variant="h5"
                        style="text-align: center; padding-bottom: 20px;"
                      >
                        Upload files
                      </studio-text>
                      ${this.processForced ? '' : this._renderDropzone({
                        mainVariant: 's1-subtitle',
                        subVariant: 'c3-caption',
                        isModal: true,
                      })}
                      ${this.pendingFiles.length > 0 ? this._renderFileList() : ''}
                    `
                  : ''}
                ${this.step === 'view'
                  ? html`
                      <div class="meta-container">
                        ${this.pendingFiles.map((file, index) => html`
                          <studio-upload-meta
                            class=${index === this.currentIndex ? 'active' : 'hidden'}
                            .config=${this.getConfigForFile(index)}
                            collection="edit"
                            ?upload=${true}
                            ?confirmOnly=${!!this.processForced}
                            @delete-asset=${this.handleDeleteAsset}
                            @update-asset=${this.handleUpdateAsset}
                            @input=${() => this._handleMetaChange()}
                          >
                            ${this._renderThumbnailSlot(file, index)}
                            <div slot="action-buttons"></div>
                            <studio-progress
                              slot="loading-bar"
                              percent=${file.progress}
                              position="left"
                              color=${file.progress === 100
                                ? 'var(--color-green-light, #00B37E)'
                                : 'var(--color-purple-light, #4727A5)'}
                            ></studio-progress>
                          </studio-upload-meta>
                        `)}
                      </div>
                    `
                  : ''}
                ${this.step === 'submit'
                  ? html`
                      <studio-text
                        variant="h5"
                        style="text-align: center; padding-bottom: 20px;"
                      >
                        Submit files
                      </studio-text>
                      ${this._renderFileList()}
                    `
                  : ''}
                <div class="actions">
                  ${this.step === 'submit'
                    ? html`
                        <studio-button
                          size="medium"
                          style-type="filled"
                          @click=${this._back}
                        >
                          Back
                        </studio-button>
                        <studio-button
                          size="medium"
                          style-type="filled"
                          @click=${this._submit}
                          state=${!allCaptionsFilled || this.pendingFiles.length === 0 ? 'disabled' : ''}
                        >
                          Submit
                        </studio-button>
                      `
                    : html`
                        <studio-button
                          size="medium"
                          style-type="filled"
                          @click=${this._back}
                        >
                          Back
                        </studio-button>
                        <studio-button
                          size="medium"
                          style-type="filled"
                          @click=${this._next}
                          .state=${this.nextButtonState}
                        >
                          Next
                        </studio-button>
                      `}
                </div>
              </div>
            </div>
          `
        : ''}
    `;
  }
}

customElements.define('studio-upload', StudioUpload);
